// Ch94 · SOCIALS STAGES — which pill a reel wears on the Reels tab.
//
// PKT-0792 names six: To review · Approved · Scheduled · Posted · Rejected ·
// Replaced by a newer version.
//
// 🔴 "APPROVED" IS A STATUS. CORRECTED 2026-09-19 AFTER I GOT THIS WRONG.
//
// This module previously said approval was "not a status, it is an applied
// command", and that `approved` did not exist in the data at all. That was an
// INVENTED MECHANISM, caught by a provider review. The schema says otherwise:
//
//   053:86-88  status IN ('awaiting_review','revised','qa_failed','corrupt',
//                         'approved','parked')
//   081:202    verdict 'P' -> 'approved'
//
// What was true was only an OBSERVATION: two Directors measured prod holding
// ZERO rows with status='approved'. That zero is a SYMPTOM of the
// verdict-clobber defect (PKT-0794 / migration 105) — applied approvals are
// overwritten back to pending — not evidence about the schema. A correct
// observation made an invented mechanism feel verified.
//
// ⇒ THE RULE, re-derived:
//     approved            = status === 'approved'   (durable once 105 lands)
//     damaged-but-approved= an applied P whose row still says pending.
//                           Kept as ITS OWN VISIBLE STATE, because rows clobbered
//                           BEFORE 105 exist and nothing else will ever tell Grant.
//
// 🔴 AND THE REASON THIS IS NOT A COMMENT FIX: reading approval off the COMMAND
// alone disagreed with `content_publisher.py:537-547`, which fires on
// status==='approved'. Two definitions of the same word, and because a command
// row SURVIVES a re-render, the command-only reading would call a re-rendered
// reel approved on footage Grant never watched.
//
// 🔴 WHY THE DISCREPANCY GETS A PILL INSTEAD OF BEING SMOOTHED OVER.
// On both applied rows the mirror's `updated_at` is LATER than the command's
// `applied_at`: one approved at 08-15 20:16 was rewritten at 22:10 back to
// awaiting_review, and one approved on 08-18 now reads `parked` — which the UI
// draws as "Rejected". AGENT-0290's proposed mechanism (the box re-publishes
// the mirror row and overwrites it with a view that never carries P) is
// INFERRED FROM TIMESTAMPS, not read from the emitter, and Agent 287 owns that
// code. So this module does not encode the mechanism. It encodes the OBSERVABLE:
// two sources disagree about the same reel. Showing that disagreement is how
// Grant finds out his approval did not stick; hiding it behind whichever source
// we happened to trust is how it stays lost.
//
// No React, so every rule is testable without a DOM.

import type { ChainPosition } from "./reelVersionChain";

/** Only the fields the stage decision reads. A structural subset of the DTO. */
export interface StageableReel {
  status: string;
  command?: {
    verdict: string;
    state: string;
    applied: boolean;
  } | null;
  /**
   * Migration 091. ABSENT means unknown (091 unapplied, or the ledger could not
   * be read); `[]` means the ledger WAS read and holds nothing. The DTO's own
   * comment: "The card must not flatten absent into 'not posted'."
   */
  posted_platforms?: string[];
}

export type SocialsStage =
  | "to_review"
  | "approved"
  | "scheduled"
  | "posted"
  | "rejected"
  | "replaced";

export interface StageVerdict {
  stage: SocialsStage;
  /**
   * Set when the applied command and the mirror row disagree about this reel.
   * The UI shows it as its own state — never silently resolved to one side.
   */
  discrepancy: null | {
    /** Plain words for Grant. No "mirror", no "row", no jargon. */
    message: string;
    /** What the row says, for the "behind the scenes" view. */
    rowStatus: string;
  };
}

/**
 * An applied `P` command. NOT the definition of approved — see the header. It is
 * how a row CLOBBERED back to pending is recognised as damaged.
 */
const APPLIED_APPROVAL = (reel: StageableReel): boolean =>
  Boolean(
    reel.command &&
      reel.command.verdict === "P" &&
      reel.command.state === "applied" &&
      reel.command.applied
  );

/** The row's own words, mapped to the pill Grant reads. */
const stageFromStatus = (status: string): SocialsStage => {
  switch (status) {
    case "approved":
      return "approved";
    case "parked":
      return "rejected";
    case "awaiting_review":
    case "revised":
      return "to_review";
    default:
      // An unrecognised status is NOT silently folded into "to review" — that
      // would put an unknown card in the pile Grant is asked to clear. It sits
      // in review, but the caller can see the raw value via the discrepancy.
      return "to_review";
  }
};

// The statuses 053 allows. `approved` is among them — see the header.
const KNOWN_STATUSES = new Set([
  "awaiting_review",
  "revised",
  "qa_failed",
  "corrupt",
  "parked",
]);

export interface StageInputs {
  /** From the version chain. A visible newer sibling outranks everything. */
  chainPosition?: ChainPosition;
  /** Phase 4. `true` only when a slot row genuinely exists for this reel. */
  hasScheduledSlot?: boolean;
}

/**
 * Decide the pill.
 *
 * Precedence, highest first — each level is a fact that makes the ones below it
 * no longer the most useful thing to say about this reel:
 *
 *   replaced  a newer version is VISIBLE (never inferred from absence)
 *   posted    the ledger says it went out
 *   scheduled a slot row exists
 *   approved  an applied P command exists
 *   rejected / to review  what the row says
 */
export function stageOf(reel: StageableReel, inputs: StageInputs = {}): StageVerdict {
  // The row is the authority. The command is only evidence that the row is DAMAGED.
  const approvedByStatus = reel.status === "approved";
  const approvedByCommandOnly = !approvedByStatus && APPLIED_APPROVAL(reel);
  const approved = approvedByStatus;
  const rowStage = stageFromStatus(reel.status);

  // The disagreement is computed independently of which stage wins, so it
  // survives being outranked by "posted" or "replaced".
  const discrepancy =
    approvedByCommandOnly && KNOWN_STATUSES.has(reel.status)
      ? {
          /*
           * The copy names a NEXT ACTION, not just a fault.
           *
           * Agent 287 established that 081:363-365 only refuses a terminal
           * verdict when the row is NOT (verdict='pending' AND status IN
           * awaiting_review/revised/qa_failed). Both destroyed rows currently
           * read pending ⇒ they are still ACTIONABLE. So this is not a dead
           * end: re-approving works, and once the guard migration lands it
           * sticks. A message that only reports damage leaves Grant nowhere.
           */
          message:
            reel.status === "parked"
              ? "You approved this, but it is filed as rejected. Something overwrote your decision — approving it again will work."
              : "You approved this, but it is back in the review pile. Something overwrote your decision — approving it again will work.",
          rowStatus: reel.status,
        }
      : null;

  if (inputs.chainPosition?.kind === "replaced") {
    return { stage: "replaced", discrepancy };
  }

  // ABSENT means unknown and must not become "not posted"; only a non-empty
  // ledger reading promotes a reel to Posted.
  if (reel.posted_platforms && reel.posted_platforms.length > 0) {
    return { stage: "posted", discrepancy };
  }

  if (inputs.hasScheduledSlot) {
    return { stage: "scheduled", discrepancy };
  }

  if (approved) {
    return { stage: "approved", discrepancy };
  }

  return { stage: rowStage, discrepancy };
}

/** The pill's words, in Grant's language. */
export const STAGE_LABELS: Record<SocialsStage, string> = {
  to_review: "To review",
  approved: "Approved",
  scheduled: "Scheduled",
  posted: "Posted",
  rejected: "Rejected",
  replaced: "Replaced by a newer version",
};

/**
 * Whether this reel may be scheduled, and if not, why — in plain words.
 *
 * Adopted from AGENT-0284's last prod read: 8 awaiting-review and all 6 revised
 * cards carry NO post kit. A scheduler that happily queues a captionless post is
 * worse than one that refuses, so the refusal is part of the model, not a
 * afterthought in the button.
 */
export function scheduleRefusal(
  reel: StageableReel,
  hasCaption: boolean
): string | null {
  /*
   * 🔴 ONE RULE, BOTH SIDES. Provider round 2 caught this offering Schedule on
   * the STATUS alone while the server required status AND an applied P — so a
   * card looked schedulable and the request came back 409 with a message the
   * operator could not act on. A client rule looser than the server's is not a
   * convenience; it is a promise the server breaks.
   *
   * These three refusals are the same three the route applies, in the same
   * order, with the same words:
   *   admin_content.py  "Only an approved reel can be scheduled" / "No caption on file yet."
   */
  if (reel.status !== "approved") return "Approve this first.";
  if (!APPLIED_APPROVAL(reel)) {
    // status says approved and nothing authorises it. The server calls this a
    // disagreement and refuses; saying so here saves a round trip.
    return "Approve this first.";
  }
  if (!hasCaption) return "No caption on file yet.";
  return null;
}
